export type Bi = { fr: string; en: string }

export const trustChallengeCopy = {
  eyebrow: { fr: 'Le défi', en: 'The challenge' },
  headline: [
    { text: { fr: 'Une mauvaise réponse', en: 'A wrong answer' }, accent: false },
    { text: { fr: 'ne fait pas gagner du temps.', en: 'doesn’t save time.' }, accent: false },
    { text: { fr: 'Elle crée du travail.', en: 'It creates work.' }, accent: true },
  ],
  body: {
    fr: 'Lorsque l’IA donne une réponse inexacte, non vérifiée ou impossible à expliquer, le problème ne disparaît pas. Il se déplace vers vos équipes.',
    en: 'When AI gives an answer that is inaccurate, unverified or impossible to explain, the problem doesn’t disappear. It moves to your teams.',
  },
}

/* External research — McKinsey, The State of AI 2025. Not BCITI | ATNA results. */
export type EvidenceIcon = 'alert' | 'doc' | 'chart'
export const trustEvidence: { icon: EvidenceIcon; value: Bi; text: Bi; source: string }[] = [
  {
    icon: 'alert',
    value: { fr: '51 %', en: '51%' },
    text: {
      fr: 'des organisations utilisant l’IA rapportent au moins une conséquence négative liée à son utilisation.',
      en: 'of organizations using AI report at least one negative consequence from its use.',
    },
    source: 'McKinsey, State of AI 2025',
  },
  {
    icon: 'doc',
    value: { fr: '≈ 1 sur 3', en: '≈ 1 in 3' },
    text: {
      fr: 'rapporte des conséquences découlant de l’inexactitude de l’IA.',
      en: 'report consequences stemming from AI inaccuracy.',
    },
    source: 'McKinsey, State of AI 2025',
  },
  {
    icon: 'chart',
    value: { fr: 'Nº 1', en: '#1' },
    text: {
      fr: 'L’inexactitude est le risque lié à l’IA que les organisations rencontrent le plus souvent.',
      en: 'Inaccuracy is the AI risk organizations most often experience.',
    },
    source: 'McKinsey, State of AI 2025',
  },
]

export type StepIcon =
  | 'question'
  | 'ai'
  | 'wrong'
  | 'call'
  | 'employee'
  | 'correction'
  | 'atna'
  | 'verify'
  | 'answer'
  | 'action'

export type JourneyStep = {
  icon: StepIcon
  title: Bi
  detail?: Bi
  checks?: Bi[]
  tone?: 'alert' | 'success' | 'indigo'
}

export const unverifiedJourney = {
  title: { fr: 'Une réponse non vérifiée', en: 'An unverified answer' },
  sub: { fr: 'Le problème se déplace vers vos équipes.', en: 'The problem moves to your teams.' },
  steps: [
    {
      icon: 'question',
      title: { fr: 'Question d’un usager', en: 'A user’s question' },
      detail: { fr: '« Où puis-je obtenir mon permis ? »', en: '“Where can I get my permit?”' },
    },
    {
      icon: 'ai',
      title: { fr: 'Agent IA', en: 'AI agent' },
      detail: { fr: '« Vous pouvez le faire en ligne ici… »', en: '“You can do it online here…”' },
    },
    {
      icon: 'wrong',
      title: { fr: 'Réponse erronée', en: 'Wrong answer' },
      detail: { fr: 'Information inexacte ou non à jour', en: 'Inaccurate or outdated information' },
      tone: 'alert',
    },
    {
      icon: 'call',
      title: { fr: 'Nouvel appel', en: 'New call' },
      detail: { fr: 'L’usager recontacte la ville', en: 'The user contacts the city again' },
    },
    {
      icon: 'employee',
      title: { fr: 'Vérification par un employé', en: 'Employee verification' },
      detail: { fr: 'Recherche de la bonne information', en: 'Searching for the right information' },
    },
    {
      icon: 'correction',
      title: { fr: 'Correction et suivi', en: 'Correction and follow-up' },
      detail: { fr: 'Réponse corrigée et suivi manuel', en: 'Corrected answer and manual follow-up' },
    },
  ] as JourneyStep[],
  result: {
    fr: 'Résultat : plus d’appels, plus de travail, moins de confiance.',
    en: 'Result: more calls, more work, less trust.',
  },
}

export const trustedJourney = {
  title: { fr: 'Une réponse de confiance', en: 'A trusted answer' },
  sub: { fr: 'L’IA vérifie avant d’agir.', en: 'AI verifies before acting.' },
  steps: [
    {
      icon: 'question',
      title: { fr: 'Question d’un usager', en: 'A user’s question' },
      detail: { fr: '« Où puis-je obtenir mon permis ? »', en: '“Where can I get my permit?”' },
    },
    {
      icon: 'atna',
      title: { fr: 'ATNA', en: 'ATNA' },
      detail: { fr: 'Sources + contexte + règles', en: 'Sources + context + rules' },
      tone: 'indigo',
    },
    {
      icon: 'verify',
      title: { fr: 'Vérification', en: 'Verification' },
      checks: [
        { fr: 'Sources fiables', en: 'Reliable sources' },
        { fr: 'Contexte autorisé', en: 'Authorized context' },
        { fr: 'Règles appliquées', en: 'Rules applied' },
        { fr: 'Niveau de confiance', en: 'Confidence level' },
      ],
    },
    {
      icon: 'answer',
      title: { fr: 'Réponse / recommandation', en: 'Answer / recommendation' },
      detail: { fr: 'Réponse claire, à jour et personnalisée', en: 'Clear, current and personalized answer' },
      tone: 'indigo',
    },
    {
      icon: 'action',
      title: { fr: 'Action autorisée', en: 'Authorized action' },
      detail: { fr: 'Demande traitée sans nouveau contact', en: 'Request completed with no follow-up contact' },
      tone: 'success',
    },
  ] as JourneyStep[],
  result: {
    fr: 'Résultat : un service plus rapide, plus fiable et plus efficace.',
    en: 'Result: faster, more reliable and more efficient service.',
  },
}
